// api.js
import axios from 'axios';

const isDevelopment = process.env.NODE_ENV !== 'production';
const defaultApiBaseUrl = isDevelopment ? 'http://localhost:8000' : 'https://whe2.onrender.com';
const logDebug = (...args) => { if (isDevelopment) console.log(...args); };
const logWarn = (...args) => { if (isDevelopment) console.warn(...args); };
const logError = (...args) => { if (isDevelopment) console.error(...args); };

const normalizeApiError = (error, fallbackMessage = 'Unexpected API error') => {
  if (error instanceof Error) {
    return error;
  }

  const normalized = new Error(
    error?.message ||
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    fallbackMessage
  );

  normalized.name = error?.name || 'ApiError';
  normalized.code = error?.code;
  normalized.config = error?.config;
  normalized.request = error?.request;
  normalized.response = error?.response;
  normalized.cause = error;

  return normalized;
};

// Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || defaultApiBaseUrl,
  TIMEOUT: 30000, 
  MAX_RETRIES: 2,
  RETRY_DELAY: 1000,
  
  HEADERS: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  },
  
  CORS_CONFIG: {
    withCredentials: true,
  }
};


// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
  ...API_CONFIG.CORS_CONFIG
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    logDebug(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }
    
    return config;
  },
  (error) => {
    logError('❌ Request Error:', error);
    return Promise.reject(normalizeApiError(error, 'Request setup failed.'));
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    logDebug(`📥 API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const normalizedError = normalizeApiError(error, 'API request failed.');
    const originalRequest = normalizedError.config;
    
    if (!originalRequest) {
      return Promise.reject(normalizedError);
    }

    if (originalRequest._retryCount >= API_CONFIG.MAX_RETRIES) {
      return Promise.reject(normalizedError);
    }
    
    if (normalizedError.response?.status && [400, 401, 403, 404].includes(normalizedError.response.status)) {
      return Promise.reject(normalizedError);
    }
    
    if (!normalizedError.response || normalizedError.response.status >= 500) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      logDebug(`🔄 Retry ${originalRequest._retryCount}/${API_CONFIG.MAX_RETRIES} for ${originalRequest.url}`);
      
      const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, originalRequest._retryCount - 1);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return apiClient(originalRequest);
    }
    
    return Promise.reject(normalizedError);
  }
);

/**
 * Check API health
 */
export const checkHealth = async () => {
  try {
    const response = await apiClient.get('/health');
    return {
      status: 'connected',
      data: response.data,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    logWarn('Health check failed:', error.message);
    return {
      status: 'disconnected',
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Get route information
 */
let activeRequests = new Map();

// Request deduplication helper
const getRequestKey = (endpoint, params) => {
  return `${endpoint}:${JSON.stringify(params)}`;
};

// Modified getRouteInfo with deduplication and route preferences
export const getRouteInfo = async (params) => {
  const {
    start,
    end,
    start_place_id,
    end_place_id,
    start_session_token,
    end_session_token,
    start_lng,
    start_lat,
    end_lng,
    end_lat,
    vehicle_class = 'Class 1',
    fuel_type = 'Petrol',
    avg_consumption = '8.0',
    max_routes = 4,
    avoid_highways = false,
    avoid_tolls = false 
  } = params;
  
  if (!start || !end) {
    throw new Error('Start and end locations are required');
  }

  // Build the exact route request payload up front so in-flight dedupe matches
  // the real backend request instead of a simplified subset of fields.
  const queryParams = {
    start: start.trim(),
    end: end.trim(),
    ...(start_place_id ? { start_place_id } : {}),
    ...(end_place_id ? { end_place_id } : {}),
    ...(start_session_token ? { start_session_token } : {}),
    ...(end_session_token ? { end_session_token } : {}),
    ...(Number.isFinite(Number(start_lng)) && Number.isFinite(Number(start_lat))
      ? { start_lng: Number(start_lng), start_lat: Number(start_lat) }
      : {}),
    ...(Number.isFinite(Number(end_lng)) && Number.isFinite(Number(end_lat))
      ? { end_lng: Number(end_lng), end_lat: Number(end_lat) }
      : {}),
    vehicle_class,
    fuel_type,
    avg_consumption: parseFloat(avg_consumption).toFixed(1),
    max_routes: Math.min(max_routes, 4),
    avoid_highways: Boolean(avoid_highways),
    avoid_tolls: Boolean(avoid_tolls),
  };

  // Remove false values to keep URL clean while preserving request identity above.
  if (!queryParams.avoid_highways) delete queryParams.avoid_highways;
  if (!queryParams.avoid_tolls) delete queryParams.avoid_tolls;

  const requestKey = getRequestKey('/get_route', queryParams);
  
  // Check if same request is already in progress
  if (activeRequests.has(requestKey)) {
    logDebug('🔁 Using existing request for:', requestKey);
    return activeRequests.get(requestKey);
  }
  
  logDebug('🚀 Fetching route info (deduplicated):', { 
    start, 
    end, 
    max_routes,
    avoid_highways,
    avoid_tolls,
    requestKey
  });
  
  try {
    // Create the promise
    const requestPromise = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000); // 35 second timeout
      
      const response = await apiClient.get('/get_route', {
        params: queryParams,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      logDebug('✅ Route info received:', {
        routeCount: response.data.routes?.length || 0,
        requestedMax: max_routes,
        hasRoutes: !!response.data.routes,
        preferences: {
          avoid_highways: avoid_highways,
          avoid_tolls: avoid_tolls
        }
      });
      
      // Add performance timing
      const enhancedData = {
        ...response.data,
        performance_metrics: {
          total_seconds: (Date.now() - performance.now()) / 1000,
          route_count: response.data.routes?.length || 0,
          timestamp: new Date().toISOString(),
          preferences_applied: {
            avoid_highways: avoid_highways,
            avoid_tolls: avoid_tolls
          }
        }
      };
      
      return enhancedData;
    })();
    
    // Store the promise
    activeRequests.set(requestKey, requestPromise);
    
    // Clean up when done
    requestPromise
      .finally(() => {
        setTimeout(() => {
          activeRequests.delete(requestKey);
        }, 1000); // Keep in cache for 1 second after completion
      })
      .catch(() => {
        // Prevent the cleanup chain from surfacing as an unhandled rejection.
      });
    
    return await requestPromise;
    
  } catch (error) {
    // Clean up on error
    activeRequests.delete(requestKey);
    
    logError('❌ Route info error:', error);
    
    if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
      throw new Error('Request timeout. The route calculation is taking too long. Please try again with fewer route alternatives.');
    }
    
    if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      throw new Error('Network error. Please check your internet connection.');
    }
    
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    
    if (error.response?.status === 504) {
      throw new Error('Route calculation timed out. Please try again or use simpler locations.');
    }
    
    // Check for specific preference-related errors
    if (error.response?.data?.detail?.includes('toll') || 
        error.response?.data?.detail?.includes('highway')) {
      throw new Error(`Route preference error: ${error.response.data.detail}. Please try different settings.`);
    }
    
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    
    throw new Error('Failed to calculate route. Please try different locations or check your connection.');
  }
};

/**
 * Get autocomplete suggestions - FIXED VERSION
 */
export const getAutocompleteSuggestions = async (query, limit = 8, sessionToken = '') => {
  if (!query || query.length < 1) {
    return { suggestions: [] };
  }
  
  try {
    logDebug(`🔍 Fetching autocomplete for: "${query}"`);
    
    const response = await apiClient.get('/search/autocomplete', {
      params: { 
        query: query.trim(), 
        limit,
        ...(sessionToken ? { session_token: sessionToken } : {})
      }
    });
    
    logDebug('✅ Autocomplete response received:', {
      query: query,
      suggestionsCount: response.data.suggestions?.length || 0,
      hasSuggestions: !!response.data.suggestions
    });
    
    return response.data;
    
  } catch (error) {
    logWarn('❌ Autocomplete error:', error.message);
    return { suggestions: [] };
  }
};

export const getFuelStationsAlongRoute = async ({
  route_geometry,
  max_detour_km = 5,
  max_stations = 24,
}) => {
  if (!Array.isArray(route_geometry) || route_geometry.length < 2) {
    return { stations: [], count: 0 };
  }

  try {
    const response = await apiClient.post('/api/fuel/stations-along-route', {
      route_geometry,
      max_detour_km,
      max_stations,
    });
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, 'Failed to load fuel stations.');
  }
};

export const getApiBaseUrl = () => API_CONFIG.BASE_URL;

/**
 * Get Mapbox token
 */
export const getMapboxToken = async () => {
  try {
    const response = await apiClient.get('/get_mapbox_token');
    return response.data;
  } catch (error) {
    logWarn('Failed to get Mapbox token:', error.message);
    return {
      mapbox_access_token: process.env.REACT_APP_MAPBOX_TOKEN || '',
      has_token: false
    };
  }
};

/**
 * Get cache performance stats (optional - if you want to monitor caching)
 */
export const getCachePerformance = async () => {
  try {
    const response = await apiClient.get('/api/cache/performance');
    return response.data;
  } catch (error) {
    logWarn('Cache performance check failed:', error.message);
    return null;
  }
};

/**
 * Clear route cache (admin/development use)
 */
export const clearRouteCache = async () => {
  try {
    const response = await apiClient.post('/api/cache/routes/clear');
    return response.data;
  } catch (error) {
    logWarn('Cache clear failed:', error.message);
    throw normalizeApiError(error, 'Failed to clear route cache.');
  }
};

export const createShareRoute = async (payload) => {
  try {
    const response = await apiClient.post('/api/share-route', {
      start: (payload.start || '').trim(),
      end: (payload.end || '').trim(),
      routeName: payload.routeName || 'Shared route',
      duration: payload.duration || 'N/A',
      distance: payload.distance || 'N/A',
      totalCost: Number.parseFloat(String(payload.totalCost ?? 0).replace(/[^\d.]/g, '')) || 0,
      fuelCost: Number.parseFloat(String(payload.fuelCost ?? 0).replace(/[^\d.]/g, '')) || 0,
      tollCost: Number.parseFloat(String(payload.tollCost ?? 0).replace(/[^\d.]/g, '')) || 0,
    });
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, 'Failed to create share link.');
  }
};

export const getSharedRoute = async (shareId) => {
  try {
    const response = await apiClient.get(`/api/share-route/${encodeURIComponent(shareId)}`);
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, 'Failed to load shared route.');
  }
};

// Export the API service
export const routeService = {
  checkHealth,
  getRouteInfo,
  getAutocompleteSuggestions,
  getFuelStationsAlongRoute,
  getApiBaseUrl,
  getMapboxToken,
  getCachePerformance,
  clearRouteCache,
  createShareRoute,
  getSharedRoute,
  apiClient
};

export default routeService;
